import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  formatCertType,
  formatGender,
  formatOptionalId,
  formatRealNameStatus,
  kycStatusClassName,
  resolveContact,
  resolveCountryLabel,
} from './kycConstants';

function DetailRow({ label, value, className = '', children }) {
  return (
    <div className="product-detail-row">
      <span className="product-detail-row__label">{label}</span>
      <span className={`product-detail-row__value ${className}`.trim()}>
        {children ?? value ?? '—'}
      </span>
    </div>
  );
}

function IdImage({ label, src }) {
  const mediaUrl = resolveAdminMediaUrl(src);
  return (
    <div className="product-detail-row kyc-id-image-row">
      <span className="product-detail-row__label">{label}</span>
      <span className="product-detail-row__value">
        {mediaUrl ? (
          <a href={mediaUrl} target="_blank" rel="noreferrer">
            <img src={mediaUrl} alt={label} className="kyc-id-image" />
          </a>
        ) : (
          '—'
        )}
      </span>
    </div>
  );
}

export default function KycDetailModal({ open, memberId, onClose, onEdit, onJourney }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !memberId) {
      setDetail(null);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    adminApi
      .kycDetail(memberId)
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || '載入失敗');
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, memberId]);

  if (!open || !memberId) {
    return null;
  }

  const statusCode = typeof detail?.realNameStatus === 'string'
    ? detail.realNameStatus
    : detail?.realNameStatus?.name;

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>詳情 [{formatOptionalId(memberId)}]</h3>
          <div className="admin-modal__header-actions">
            <button type="button" className="btn btn-success btn-sm" onClick={() => onEdit?.(detail || { memberId })}>
              修改
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => onJourney?.(memberId)}>
              認證紀錄
            </button>
            <button type="button" className="admin-modal__close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className="admin-modal__body">
          {loading && <p className="page-desc">載入中…</p>}
          {error && <p className="form-error">{error}</p>}
          {!loading && !error && detail && (
            <div className="product-detail-grid">
              <DetailRow label="用戶ID" value={formatOptionalId(detail.memberId)} />
              <DetailRow label="國家" value={resolveCountryLabel(detail)} />
              <DetailRow label="郵箱/手機號" value={resolveContact(detail)} />
              <DetailRow label="性別" value={formatGender(detail.gender)} />
              <div className="kyc-id-images">
                <IdImage label="證件(正面)" src={detail.identityImageFront} />
                <IdImage label="證件(反面)" src={detail.identityImageBack} />
              </div>
              <DetailRow label="備註" value={detail.remark} />
              <DetailRow
                label="認證狀態"
                value={formatRealNameStatus(statusCode)}
                className={kycStatusClassName(statusCode)}
              />
              <DetailRow label="更新時間" value={formatDateTime(detail.updateTime)} />
            </div>
          )}
        </div>
        <div className="admin-modal__footer admin-modal__footer--center">
          <button type="button" className="btn btn-danger" onClick={onClose}>
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
